import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useSelector } from 'react-redux';
import TagManagementSection from '../Common/TagManagementSection';
import JourneyMapDueDateField from './JourneyMapDueDateField';
import { CacheKey } from '../../../types/cache-keys';
import { RootState, useAppDispatch } from '../../../support/store';
import {
  resetBuilderState,
  setActiveDraft,
  updateJourneyMapDraftField,
} from '../../../support/slices/journeyMapBuilderSlice';
import {
  addJourneyMapType,
  setJourneyMapTypes,
  upsertJourneyMap,
} from '../../../support/slices/journeyMapsSlice';
import { fetchItems, postItem } from '../../../support/queries';
import JourneyMapCanvas from './Canvas/JourneyMapCanvas';
import JourneyMapToolbar from './JourneyMapToolbar';
import JourneyMapSidePanel from './SidePanel';
import {
  loadJourneyMapDraft,
  saveJourneyMapDraft,
} from '../../../support/journeyMapPersistence';
import { validateJourneyMapDraft } from '../../../support/journeyMapValidation';
import { useActiveJourneyMapDraft } from '../../../hooks/journey-map-hooks';

const ADD_NEW_TYPE_VALUE = '__add_new_type__';

type SectionDelimiterColor =
  | 'neutral'
  | 'interact'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger';

type SectionDelimiterRecord = {
  tempId: string;
  x?: number;
  y?: number;
  label?: string;
  orientation?: 'vertical' | 'horizontal';
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  width?: number;
  color?: SectionDelimiterColor;
};

export default function JourneyMapBuilderPage() {
  const dispatch = useAppDispatch();
  const draft = useActiveJourneyMapDraft();
  const journeyTypes = useSelector((state: RootState) => state.journeyMaps.types || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const types = await fetchItems('db/journey-map-type/');
        if (active) {
          dispatch(setJourneyMapTypes(types || []));
        }
      } catch (e) {
        // no-op
      }
    })();

    return () => {
      active = false;
    };
  }, [dispatch]);

  // The save-status banner is transient: it clears itself 10s after it appears so a stale
  // "saved" / error message does not linger over the canvas. It can also be dismissed by hand
  // (the Alert's close X). Re-armed on each new message; cleared on unmount.
  useEffect(() => {
    if (!error && !success) return;

    const timer = window.setTimeout(() => {
      setError('');
      setSuccess('');
    }, 10000);

    return () => window.clearTimeout(timer);
  }, [error, success]);

  const issues = useMemo(() => {
    if (!draft) return [];
    return validateJourneyMapDraft(draft);
  }, [draft]);

  const selectedTypeName = useMemo(() => {
    if (!draft?.journey_type) return '';
    const match = journeyTypes.find((t: any) => t.id === draft.journey_type);
    return match?.name || '';
  }, [draft?.journey_type, journeyTypes]);

  const activeItemCount = useMemo(
    () => draft?.items.filter((item) => !item.isDeleted).length || 0,
    [draft],
  );

  const activeLinkCount = useMemo(
    () => draft?.links.filter((link) => !link.isDeleted).length || 0,
    [draft],
  );

  const activePathGroupCount = useMemo(
    () => draft?.pathGroups.filter((group) => !group.isDeleted).length || 0,
    [draft],
  );

  const isEntitySelected = useMemo(() => {
    return !!(
      draft?.selectedItemTempId ||
      draft?.selectedLinkTempId ||
      draft?.selectedPathGroupTempId
    );
  }, [draft]);

  const canSave = useMemo(() => {
    if (!draft) return false;
    return !!draft.name?.trim() && !!draft.journey_type && !saving;
  }, [draft, saving]);

  const sectionDelimiters = useMemo<SectionDelimiterRecord[]>(
    () => (Array.isArray((draft as any)?.section_delimiters) ? (draft as any).section_delimiters : []),
    [draft],
  );

  const handleAddJourneyType = async () => {
    const name = window.prompt('Enter new Journey Type name');
    if (!name?.trim()) return;

    try {
      const created = await postItem('db/journey-map-type', {
        name: name.trim(),
      });
      dispatch(addJourneyMapType(created));
      dispatch(updateJourneyMapDraftField({ field: 'journey_type', value: created.id }));
    } catch (e) {
      setError('Failed to create Journey Type.');
    }
  };

  const handleJourneyTypeChange = async (e: SelectChangeEvent<string | number>) => {
    if (e.target.value === ADD_NEW_TYPE_VALUE) {
      await handleAddJourneyType();
      return;
    }

    dispatch(
      updateJourneyMapDraftField({
        field: 'journey_type',
        value: Number(e.target.value) || null,
      }),
    );
  };

  const handleSave = async () => {
    if (!draft) return;

    setError('');
    setSuccess('');

    const validationIssues = validateJourneyMapDraft(draft);
    if (validationIssues.length) {
      setError(validationIssues.map((i) => i.message).join(' '));
      return;
    }

    try {
      setSaving(true);
      const { map: saved, droppedLinks, linkConflicts } = await saveJourneyMapDraft(draft);
      dispatch(upsertJourneyMap(saved));

      // Re-hydrate the draft from the server so items and links carry their real ids.
      // Without this, every item stays tempId-only and the next save deletes and
      // recreates the whole map (losing item progress and churning links).
      const reloaded = await loadJourneyMapDraft(saved);
      dispatch(setActiveDraft(reloaded));

      if (droppedLinks.length) {
        setError(
          `Journey Map saved, but ${droppedLinks.length} link(s) could not be connected ` +
            'because their start or end item was missing. Re-draw those links and save again.',
        );
      } else if (linkConflicts.length) {
        setError(
          `Journey Map saved, but ${linkConflicts.length} link(s) between an already-linked ` +
            'pair of items were rejected. Multiple links between the same two items are not ' +
            'supported yet.',
        );
      } else {
        setSuccess('Journey Map saved successfully.');
      }
    } catch (e) {
      setError('Failed to save Journey Map.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSectionDelimiter = () => {
    const next: SectionDelimiterRecord[] = [
      ...sectionDelimiters,
      {
        tempId: `delimiter_${Date.now()}`,
        orientation: 'vertical',
        x: 300,
        y: 180,
        label: '',
        lineStyle: 'solid',
        width: 2,
        color: 'neutral',
      },
    ];

    dispatch(
      updateJourneyMapDraftField({
        field: 'section_delimiters',
        value: next,
      }),
    );
  };

  const handleUpdateSectionDelimiter = (tempId: string, patch: Partial<SectionDelimiterRecord>) => {
    const next = sectionDelimiters.map((delimiter) =>
      delimiter.tempId === tempId ? { ...delimiter, ...patch } : delimiter
    );

    dispatch(
      updateJourneyMapDraftField({
        field: 'section_delimiters',
        value: next,
      }),
    );
  };

  const handleRemoveSectionDelimiter = (tempId: string) => {
    const next = sectionDelimiters.filter((delimiter) => delimiter.tempId !== tempId);

    dispatch(
      updateJourneyMapDraftField({
        field: 'section_delimiters',
        value: next,
      }),
    );
  };

  if (!draft) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No active Journey Map draft selected.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: '#fff',
        }}
      >
        <JourneyMapToolbar
          name={draft.name}
          publicationStatus={draft.publication_status}
          itemCount={activeItemCount}
          linkCount={activeLinkCount}
          pathGroupCount={activePathGroupCount}
          isDirty={draft.isDirty}
          isNew={draft.isNew}
          version={draft.version}
          isActive={draft.is_active}
          typeName={selectedTypeName}
          mapId={draft.id || null}
          isSaving={saving}
          canSave={canSave}
          validationMessage={issues.length ? issues.map((i) => i.message).join(' ') : null}
          onSave={handleSave}
        />
      </Box>

      {(error || success) && (
        <Box sx={{ p: 1 }}>
          {!!error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {!error && !!success && (
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 340px',
          gap: 1,
          p: 1,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ minWidth: 0, minHeight: 0 }}>
          <JourneyMapCanvas
            backgroundImageUrl={(draft as any).background_image_url || null}
            sectionDelimiters={sectionDelimiters}
          />
        </Box>

        <Box
          sx={{
            minHeight: 0,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            background: '#fff',
          }}
        >
          {!isEntitySelected ? (
            <Box sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Journey Map Settings
                </Typography>

                <TextField
                  size="small"
                  label="Name"
                  value={draft.name}
                  onChange={(e) =>
                    dispatch(updateJourneyMapDraftField({ field: 'name', value: e.target.value }))
                  }
                  fullWidth
                />

                <TextField
                  size="small"
                  label="Description"
                  value={draft.description || ''}
                  onChange={(e) =>
                    dispatch(
                      updateJourneyMapDraftField({
                        field: 'description',
                        value: e.target.value,
                      }),
                    )
                  }
                  fullWidth
                  multiline
                  minRows={3}
                />

                <JourneyMapDueDateField mapId={draft.id || null} mapName={draft.name} />

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Tags
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    An architecture with the same tag appears on this map&apos;s page, and this map appears on
                    that architecture&apos;s page.
                  </Typography>
                  <TagManagementSection
                    parentId={draft.id || null}
                    parentType={CacheKey.JourneyMap}
                    disabled={!draft.id}
                  />
                  {!draft.id && (
                    <Typography variant="caption" color="text.secondary">
                      Save the map once to add tags.
                    </Typography>
                  )}
                </Box>

                <FormControl size="small" fullWidth>
                  <InputLabel id="journey-status-side-label">Status</InputLabel>
                  <Select
                    labelId="journey-status-side-label"
                    label="Status"
                    value={draft.publication_status || 'draft'}
                    onChange={(e: SelectChangeEvent<string>) =>
                      dispatch(
                        updateJourneyMapDraftField({
                          field: 'publication_status',
                          value: e.target.value,
                        }),
                      )
                    }
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel id="journey-type-side-label">Journey Type</InputLabel>
                  <Select
                    labelId="journey-type-side-label"
                    label="Journey Type"
                    value={draft.journey_type ? String(draft.journey_type) : ''}
                    onChange={handleJourneyTypeChange}
                  >
                    {journeyTypes.map((type: any) => (
                      <MenuItem key={type.id} value={String(type.id)}>
                        {type.name}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem value={ADD_NEW_TYPE_VALUE}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddCircleOutlineIcon fontSize="small" />
                        + Add New
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <Divider />

                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Canvas Appearance
                </Typography>

                <TextField
                  size="small"
                  label="Background Image URL"
                  value={(draft as any).background_image_url || ''}
                  onChange={(e) =>
                    dispatch(
                      updateJourneyMapDraftField({
                        field: 'background_image_url',
                        value: e.target.value,
                      }),
                    )
                  }
                  fullWidth
                  placeholder="https://example.com/journey-background.png"
                />

                <Divider />

                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Section Delimiters
                    </Typography>

                    <Button size="small" variant="outlined" onClick={handleAddSectionDelimiter}>
                      Add Divider
                    </Button>
                  </Box>

                  {sectionDelimiters.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No section delimiters configured.
                    </Typography>
                  ) : (
                    sectionDelimiters.map((delimiter) => {
                      const orientation = delimiter.orientation || 'vertical';

                      return (
                        <Box
                          key={delimiter.tempId}
                          sx={{
                            p: 1.25,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            background: '#fafafa',
                          }}
                        >
                          <Stack spacing={1}>
                            <TextField
                              size="small"
                              label="Label"
                              value={delimiter.label || ''}
                              onChange={(e) =>
                                handleUpdateSectionDelimiter(delimiter.tempId, {
                                  label: e.target.value,
                                })
                              }
                              fullWidth
                            />

                            <FormControl size="small" fullWidth>
                              <InputLabel>Orientation</InputLabel>
                              <Select
                                label="Orientation"
                                value={orientation}
                                onChange={(e) =>
                                  handleUpdateSectionDelimiter(delimiter.tempId, {
                                    orientation: e.target.value as 'vertical' | 'horizontal',
                                  })
                                }
                              >
                                <MenuItem value="vertical">Vertical</MenuItem>
                                <MenuItem value="horizontal">Horizontal</MenuItem>
                              </Select>
                            </FormControl>

                            <TextField
                              size="small"
                              label="X Position"
                              type="number"
                              value={delimiter.x ?? 300}
                              onChange={(e) =>
                                handleUpdateSectionDelimiter(delimiter.tempId, {
                                  x: Number(e.target.value) || 0,
                                })
                              }
                              fullWidth
                            />

                            <TextField
                              size="small"
                              label="Y Position"
                              type="number"
                              value={delimiter.y ?? 180}
                              onChange={(e) =>
                                handleUpdateSectionDelimiter(delimiter.tempId, {
                                  y: Number(e.target.value) || 0,
                                })
                              }
                              fullWidth
                            />

                            <FormControl size="small" fullWidth>
                              <InputLabel>Line Style</InputLabel>
                              <Select
                                label="Line Style"
                                value={delimiter.lineStyle || 'solid'}
                                onChange={(e) =>
                                  handleUpdateSectionDelimiter(delimiter.tempId, {
                                    lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted',
                                  })
                                }
                              >
                                <MenuItem value="solid">Solid</MenuItem>
                                <MenuItem value="dashed">Dashed</MenuItem>
                                <MenuItem value="dotted">Dotted</MenuItem>
                              </Select>
                            </FormControl>

                            <TextField
                              size="small"
                              label="Line Width"
                              type="number"
                              value={delimiter.width ?? 2}
                              onChange={(e) =>
                                handleUpdateSectionDelimiter(delimiter.tempId, {
                                  width: Number(e.target.value) || 1,
                                })
                              }
                              fullWidth
                            />

                            <FormControl size="small" fullWidth>
                              <InputLabel>Color</InputLabel>
                              <Select
                                label="Color"
                                value={delimiter.color || 'neutral'}
                                onChange={(e) =>
                                  handleUpdateSectionDelimiter(delimiter.tempId, {
                                    color: e.target.value as SectionDelimiterColor,
                                  })
                                }
                              >
                                <MenuItem value="neutral">Neutral</MenuItem>
                                <MenuItem value="interact">Interact</MenuItem>
                                <MenuItem value="info">Info</MenuItem>
                                <MenuItem value="success">Success</MenuItem>
                                <MenuItem value="warning">Warning</MenuItem>
                                <MenuItem value="danger">Danger</MenuItem>
                              </Select>
                            </FormControl>

                            <Box>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleRemoveSectionDelimiter(delimiter.tempId)}
                              >
                                Remove Divider
                              </Button>
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })
                  )}
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  A Journey Map Name and Journey Type are required before saving.
                </Typography>
              </Stack>
            </Box>
          ) : (
            <JourneyMapSidePanel />
          )}
        </Box>
      </Box>
    </Box>
  );
}